import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    PaginationState,
    useReactTable,
} from '@tanstack/react-table'
import { IConfigChangeHistory } from '../../types/configChangeHistory'
import React, { useMemo, useState } from 'react'
import { useAppSelector } from '../../hooks/useAppSelector'
import {
    selectConfigChangeHistory,
    selectConfigChangeHistoryLoading,
    selectConfigChangeHistoryRecords,
    selectFilterFields,
} from '../../store/selectors/configChangesHistorySelectors'
import { fuzzyFilter } from '../../utils/fuzzyFilter'
import { NoData } from '../NoData'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFile } from '@fortawesome/free-solid-svg-icons/faFile'
import styles from './styles/index.module.scss'
import { CopyStateIdBtn } from './CopyStateIdBtn'
import { CustomPagination } from '../CustomPagination/CustomPagination'
import { useAppDispatch } from '../../hooks/useAppDispatch'
import { configChangeHistoryActions } from '../../store/slices/configChangeHistorySlice'
import { fetchConfigChangeHistory } from '../../store/async-thunks/configChangeHistory/fetchConfigChangeHistory'
import Loader from '../Loader/Loader'
import { CustomToggle } from '../ui/ContextAwareAccordion/CustomToggle'
import { StateIdPopover } from './StateIdPopover'

interface Props {
    guid: string
}

export const ConfigChangeHistoryTable = ({ guid }: Props) => {
    const dispatch = useAppDispatch()
    const isLoading = useAppSelector(selectConfigChangeHistoryLoading)
    const filterFields = useAppSelector(selectFilterFields)
    const configChangeHistory = useAppSelector(selectConfigChangeHistory)
    const configChangeRecords = useAppSelector(selectConfigChangeHistoryRecords)
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: filterFields.page - 1,
        pageSize: filterFields.limit,
    })

    const columns = useMemo<ColumnDef<IConfigChangeHistory>[]>(
        () => [
            {
                id: 'updated_at',
                accessorKey: 'updated_at',
                cell: (info) => info.getValue(),
                header: () => 'UPDATE TIME',
                footer: (props) => props.column.id,
            },
            {
                id: 'login',
                accessorFn: (row) => row.login,
                cell: (info) => info.getValue(),
                header: () => 'INITIATOR',
                footer: (props) => props.column.id,
            },
            {
                id: 'request_type',
                accessorKey: 'request_type',
                header: () => 'TYPE',
                footer: (props) => props.column.id,
            },
            {
                id: 'files',
                accessorFn: (row) => {
                    const flNames = Object.keys(
                        row.configuration_hash_map ?? {}
                    )
                    const hashes = Object.keys(row.configuration_hash_map ?? {})
                    const firstThree = flNames.reduce((acc: any, flName, i) => {
                        if (i < 3) {
                            acc.push(
                                <div className="d-flex gap-2 p-1">
                                    <FontAwesomeIcon
                                        icon={faFile}
                                        style={{ color: '#2C405A80' }}
                                        className="mt-1"
                                    />
                                    <span>{flName}</span>
                                </div>
                            )
                        }
                        return acc
                    }, [])

                    return flNames.length > 3 ? (
                        <>
                            {firstThree}
                            <CustomToggle
                                uniqueID={flNames[0] + hashes[0]}
                                ariaID={flNames[0] + hashes[0]}
                                showHideKeys={['buttons.show', 'buttons.hide']}
                            >
                                {flNames.slice(3).map((flName) => (
                                    <div className="d-flex gap-2 p-1">
                                        <FontAwesomeIcon
                                            icon={faFile}
                                            style={{ color: '#2C405A80' }}
                                            className="mt-1"
                                        />
                                        <span>{flName}</span>
                                    </div>
                                ))}
                            </CustomToggle>
                        </>
                    ) : (
                        firstThree
                    )
                },
                cell: (info) => info.getValue(),
                header: () => 'FILES',
                footer: (props) => props.column.id,
            },
            {
                id: 'state_id',
                accessorFn: (row) => {
                    const hashes = Object.values(
                        row.configuration_hash_map ?? {}
                    )
                    return (
                        !!hashes.length && (
                            <CustomToggle
                                uniqueID={hashes[0] ?? ''}
                                ariaID={hashes[0] ?? ''}
                                showHideKeys={[
                                    'buttons.showStateID',
                                    'buttons.hideStateID',
                                ]}
                                style={{ width: '98px' }}
                            >
                                {hashes.map((hash: string) => {
                                    return (
                                        <div className="d-flex align-items-center gap-1">
                                            <span
                                                style={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    verticalAlign:
                                                        'text-bottom',
                                                }}
                                            >
                                                {hash}
                                            </span>
                                            <CopyStateIdBtn data={hash} />
                                        </div>
                                    )
                                })}
                            </CustomToggle>
                        )
                    )
                },
                cell: (info) => info.getValue(),
                header: () => <StateIdPopover />,
                footer: (props) => props.column.id,
            },
            {
                id: 'request_status',
                accessorFn: (row) => {
                    const status = row.request_status
                    const msg = row.answer_message
                    return (
                        <>
                            {status && (
                                <div
                                    className={`${styles.status} ${styles[status]}`}
                                >
                                    {status}
                                </div>
                            )}
                            {msg && (
                                <div className="mt-1">
                                    <strong>Message: </strong>
                                    {msg}
                                </div>
                            )}
                        </>
                    )
                },
                cell: (info) => info.getValue(),
                header: () => 'STATUS',
                footer: (props) => props.column.id,
            },
        ],
        []
    )

    const table = useReactTable({
        filterFns: { fuzzy: fuzzyFilter },
        columns,
        data: configChangeRecords,
        debugTable: true,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onPaginationChange: setPagination,
        state: { pagination },
    })

    const handlePageChange = (page: number) => {
        table.setPageIndex(page - 1)
        dispatch(configChangeHistoryActions.setPage(page))
        dispatch(
            fetchConfigChangeHistory({
                productId: guid,
                from: filterFields?.from,
                to: filterFields?.to,
                login: filterFields?.login,
                status: filterFields?.status,
                limit: filterFields.limit,
                page,
            })
        )
    }

    const handleLimitChange = (limit: number) => {
        table.setPageSize(limit)
        dispatch(configChangeHistoryActions.setPage(1))
        dispatch(configChangeHistoryActions.setLimit(limit))
        dispatch(
            fetchConfigChangeHistory({
                productId: guid,
                from: filterFields?.from,
                to: filterFields?.to,
                login: filterFields?.login,
                status: filterFields?.status,
                page: 1,
                limit,
            })
        )
    }

    if (isLoading)
        return (
            <div className="vh-full d-flex justify-content-center">
                <Loader />
            </div>
        )

    return (
        <div className="mx-2">
            {!!configChangeRecords?.length ? (
                <>
                    <table className="table-round">
                        <thead className="thead-round">
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                        return (
                                            <th
                                                key={header.id}
                                                colSpan={header.colSpan}
                                                className="th-round"
                                            >
                                                {header.isPlaceholder ? null : (
                                                    <div>
                                                        {flexRender(
                                                            header.column
                                                                .columnDef
                                                                .header,
                                                            header.getContext()
                                                        )}
                                                    </div>
                                                )}
                                            </th>
                                        )
                                    })}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map((row) => {
                                return (
                                    <tr
                                        key={row.id}
                                        className="tr-round"
                                    >
                                        {row.getVisibleCells().map((cell) => {
                                            return (
                                                <td
                                                    key={cell.id}
                                                    className="tcell-round"
                                                >
                                                    <div>
                                                        {flexRender(
                                                            cell.column
                                                                .columnDef.cell,
                                                            cell.getContext()
                                                        )}
                                                    </div>
                                                </td>
                                            )
                                        })}
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                    <CustomPagination
                        totalItems={configChangeHistory.total_count}
                        itemsPerPage={filterFields.limit}
                        currentPage={filterFields.page}
                        onPageChange={handlePageChange}
                        onItemsPerPageChange={handleLimitChange}
                    />
                </>
            ) : (
                <NoData style={{ height: '300px' }} />
            )}
        </div>
    )
}
